import type { FracSimulation } from './types';
import { buildSimulationPlayback } from './simulationPlayback';
import {
  calculateResultRange,
  type ResultPropertyId,
  type ResultRange,
  type ResultRangeMethod,
} from './resultProperties';

export type ScenarioStatus = 'SUCCEEDED' | 'FAILED' | 'INVALID_SCENARIO';
export type FeasibilityStatus = 'FEASIBLE' | 'FEASIBLE_WITH_WARNINGS' | 'INFEASIBLE' | 'SIMULATION_FAILED';

export type ScenarioMetrics = Partial<Record<
  'halfLength' | 'height' | 'width' | 'netPressure' | 'heightContainment' |
  'flowBalance' | 'initiationBalance' | 'stressShadowInterference' |
  'fractureCoverage' | 'encounterCount' | 'crossCount' | 'deflectCount' |
  'openCount' | 'arrestCount' | 'crossRatio' | 'deflectRatio' | 'openRatio' |
  'arrestRatio' | 'interactionComplexityIndex' | 'conductivity' | 'efficiency' |
  'effectiveHalfLength' | 'containedHeight' | 'initiatedClusterRatio' |
  'createdFracturePerFluid' | 'effectiveHalfLengthPerFluid' | 'coveragePerFluid' |
  'conductivityPerProppant' | 'totalFluid' | 'totalProppant',
  number | null
>>;

export type ScenarioResult = {
  schemeId?: string;
  caseId?: string;
  rank?: number;
  status: ScenarioStatus;
  parameters?: Record<string, number>;
  parameter?: string;
  value?: number;
  unit?: string;
  simulationId?: number;
  cacheHit?: boolean;
  metrics?: ScenarioMetrics;
  score?: number;
  diagnostics?: Array<Record<string, unknown>>;
  provenance?: Record<string, unknown>;
  feasibilityStatus?: FeasibilityStatus;
  engineeringScreening?: 'PASS' | 'PASS_WITH_WARNINGS' | 'REJECTED';
  warnings?: Array<Record<string, unknown>>;
  rejectionReasons?: Array<Record<string, unknown>>;
  constraintViolations?: Array<Record<string, unknown>>;
  constraintCount?: number;
  recommendationReasons?: string[];
  fullWellSummary?: Record<string, any>;
};

export type ParameterSweepResponse = {
  method: string;
  optimizationRunId?: string;
  combinationCount: number;
  completedCases: number;
  failedCases: number;
  invalidCases: number;
  totalCases: number;
  schemes: ScenarioResult[];
  topSchemes: ScenarioResult[];
  weights: Record<string, number>;
  metricDefinitions: Record<string, { direction: string; capability: string; unit: string; formula?: string }>;
  baselineScheme?: ScenarioResult | null;
  objectiveTemplate?: string;
  constraints?: Record<string, unknown>;
};

export type SensitivityResponse = {
  method: string;
  parameter: string;
  unit: string;
  metric: string;
  completedCases: number;
  failedCases: number;
  invalidCases: number;
  totalCases: number;
  cases: ScenarioResult[];
};

export const OPTIMIZATION_VARIABLES = [
  { id: 'cluster_spacing_m', label: '簇间距', unit: 'm', defaults: '6, 8, 10, 12, 15' },
  { id: 'pump_rate_m3min', label: '施工排量', unit: 'm³/min', defaults: '8, 10, 12, 14, 16' },
  { id: 'n_clusters', label: '每段簇数', unit: '簇', defaults: '3, 4, 5' },
  { id: 'fluid_volume_multiplier', label: '液量倍率', unit: '倍', defaults: '0.8, 1, 1.2' },
  { id: 'proppant_mass_multiplier', label: '砂量倍率', unit: '倍', defaults: '0.8, 1, 1.2', capability: 'PARTIAL' },
] as const;

export const SENSITIVITY_PARAMETERS = [
  { id: 'nf_orientation_offset_deg', label: '天然裂缝走向偏移', unit: '°', defaults: '-20, -10, 0, 10, 20', kind: 'NF' },
  { id: 'nf_dip_offset_deg', label: '天然裂缝倾角偏移', unit: '°', defaults: '-20, -10, 0, 10, 20', kind: 'NF' },
  { id: 'nf_intensity_multiplier', label: '天然裂缝强度倍率', unit: '倍', defaults: '0.5, 0.75, 1, 1.25, 1.5', kind: 'NF', requiresOverride: true },
  { id: 'nf_friction_coefficient', label: '天然裂缝摩擦系数', unit: '无量纲', defaults: '0.4, 0.5, 0.6, 0.7, 0.8', kind: 'NF', requiresOverride: true },
  { id: 'nf_cohesion_mpa', label: '天然裂缝黏聚力', unit: 'MPa', defaults: '0.5, 1, 1.5, 2', kind: 'NF', requiresOverride: true },
  { id: 'stress_difference_mpa', label: '水平应力差', unit: 'MPa', defaults: '2, 4, 6, 8, 10', kind: 'GEOLOGY' },
  { id: 'cluster_spacing_m', label: '簇间距', unit: 'm', defaults: '6, 8, 10, 12, 15', kind: 'ENGINEERING' },
  { id: 'pump_rate_m3min', label: '施工排量', unit: 'm³/min', defaults: '8, 10, 12, 14, 16', kind: 'ENGINEERING' },
  { id: 'n_clusters', label: '每段簇数', unit: '簇', defaults: '3, 4, 5, 6', kind: 'ENGINEERING' },
  { id: 'fluid_volume_multiplier', label: '液量倍率', unit: '倍', defaults: '0.8, 1, 1.2', kind: 'ENGINEERING' },
  { id: 'proppant_mass_multiplier', label: '砂量倍率', unit: '倍', defaults: '0.8, 1, 1.2', kind: 'ENGINEERING', capability: 'PARTIAL' },
] as const;

export const parseScenarioValues = (raw: string): number[] => raw.split(',')
  .map((item) => Number(item.trim()))
  .filter((item) => Number.isFinite(item));

export const combinationCount = (values: Record<string, string>, selected: string[]): number =>
  selected.reduce((count, id) => count * parseScenarioValues(values[id] ?? '').length, 1);

export const comparisonGlobalRange = (
  simulations: FracSimulation[],
  propertyId: ResultPropertyId,
  method: ResultRangeMethod = 'MIN_MAX',
  manualMin?: number,
  manualMax?: number,
): ResultRange => {
  const frames = simulations.flatMap((simulation) => buildSimulationPlayback(simulation.results ?? []).frames);
  return calculateResultRange({ frames, durationS: Math.max(0, ...frames.map((frame) => frame.timeS)), hasTimeSeries: frames.length > 1 }, propertyId, {
    scope: 'GLOBAL', method, manualMin, manualMax,
  });
};

export type InteractionDetailRow = { label: string; value: string };

export const interactionDetailRows = (event: Record<string, unknown>): InteractionDetailRow[] => {
  const position = event.intersectionPosition as Record<string, unknown> | undefined;
  const criterion = event.criterion as Record<string, unknown> | undefined;
  const candidates: Array<[string, unknown, string]> = [
    ['交互编号', event.interactionId, ''], ['压裂段', event.stage, ''], ['射孔簇', event.cluster, ''],
    ['时间', event.time, ' s'], ['水力裂缝编号', event.hydraulicFractureId, ''],
    ['天然裂缝编号', event.naturalFractureId, ''], ['交点测深', position?.md, ' m'],
    ['交点垂深', position?.tvd, ' m'], ['相交角', event.intersectionAngle, '°'],
    ['交互类型', event.interactionType, ''], ['裂缝压力', event.pressureMpa, ' MPa'],
    ['净压力', event.netPressureMpa, ' MPa'], ['局部应力', event.localStressMpa, ' MPa'],
    ['水平应力差', event.stressDifferenceMpa, ' MPa'], ['天然裂缝摩擦系数', event.nfFrictionCoefficient, ''],
    ['天然裂缝黏聚力', event.nfCohesionMpa, ' MPa'], ['判据/原因', criterion?.reason ?? event.reason, ''],
    ['置信度', event.confidence, ''], ['天然裂缝模型编号', event.nfModelId, ''], ['天然裂缝模型版本', event.nfModelVersion, ''],
  ];
  return candidates
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    .map(([label, value, suffix]) => ({ label, value: `${String(value)}${suffix}` }));
};

export const shouldResetViewerCamera = (previousSimulationId: number | null, nextSimulationId: number | null): boolean =>
  previousSimulationId !== nextSimulationId;

export const shouldResetPlayback = shouldResetViewerCamera;
